import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { errorView } from "../helpers/ErrorHandler";
import {
  getRefreshTokenInLocal,
  getTenantId,
  getTokenInLocal,
  getUserTenantId,
  removeTokens,
  saveCompanyTokenInLocal,
  saveRefreshTokenInLocal,
  saveTokenExpiration,
  saveTokenInLocal
} from "../utils/cacheStorage";
import { axiosAuth } from "./apiService";

const getBearToken = (token: string | null) => (token ? `Bearer ${token}` : null);

const handleLogout = () => {
  try {
    removeTokens();
  } finally {
    if (window.location.pathname !== "/auth") {
      window.location.replace("/auth");
    }
  }
};

export const loadTokens = async () => {
  const tokenString = await getTokenInLocal();
  return tokenString ? JSON.parse(tokenString) : null;
};

export const saveTokens = async (tokens: any) => {
  await saveTokenInLocal(tokens);
};

export const clearTokens = async () => {
  await removeTokens();
};

export const getValidAccessToken = async () => {
  const tokenString = await getTokenInLocal();
  if (!tokenString) {
    await loadTokens();
  }
  if (!tokenString) {
    return null;
  }
  let isExpired = true;
  try {
    const decoded = jwtDecode(tokenString);
    const now = moment().unix();
    isExpired = !(decoded.exp && decoded.exp > now + 30); // add 30s buffer
  } catch (e) {}
  if (!isExpired) {
    return tokenString;
  }
  return refreshTokens();
};

export const refreshAccessToken = async () => {
  const tokenString = await getTokenInLocal();

  if (!tokenString) {
    await loadTokens();
  }
  return refreshTokens();
};

const refreshTokens = async () => {
  try {
    const token = await getTokenInLocal();
    const refreshToken = getRefreshTokenInLocal();

    if (!refreshToken) {
      errorView("Session expired, please login again");
      handleLogout();
      return null;
    }
    const isCompanyChanged = getUserTenantId() !== getTenantId();
    const response = await axiosAuth.post(
      `/auth/refresh-token`,
      { refreshToken, tenantId: isCompanyChanged ? getUserTenantId() : null },
      {
        headers: {
          authorization: getBearToken(token)
        }
      }
    );
    if (response.status === 201 && response.data?.data) {
      const { token, refreshToken, expiredIn } = response.data.data;
      saveTokenInLocal(token);
      saveCompanyTokenInLocal(token);
      saveRefreshTokenInLocal(refreshToken);
      saveTokenExpiration(expiredIn);
      return token;
    } else {
      handleLogout();
      errorView("Token not refreshed, please login again");
    }
  } catch (error) {
    handleLogout();
    errorView("Token not refreshed, please login again");
  }
};

export const handleInvalidTokens = () => {
  handleLogout();
};
